import React, {useEffect, useState} from 'react';
import { PaginatedSource } from "@/hooks/usePaginatedSource";

type PaginationControlProps = {
    source: PaginatedSource<any>;
    perPageOptions?: number[];
}

type PageNumberProps = {
    page: number;
    isActive: boolean;
    isDisabled: boolean;
    onClick: () => void;
    isFirstPage?: boolean;
    isLastPage?: boolean;
}

const PageNumber = ({ page, isActive, isDisabled, onClick, isFirstPage, isLastPage }: PageNumberProps) => {
    return (
        <button
            onClick={onClick}
            disabled={isDisabled}
            className={`
                hidden md:flex items-center justify-center w-9 h-8 text-sm font-medium border transition-colors duration-200
                ${isActive
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                : isDisabled
                    ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
            }
                ${isFirstPage ? '' : 'border-l-0'}
                ${isLastPage ? 'rounded-r-lg' : ''}
                ${isFirstPage ? 'rounded-l-lg' : ''}
            `}
        >
            {page}
        </button>
    );
};

const Ellipsis = () => {
    return (
        <span className="hidden md:flex items-center justify-center w-9 h-8 text-sm font-medium border border-l-0 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400">
            ...
        </span>
    );
};

export default function PaginationControl({
                                              source,
                                              perPageOptions = [15, 25, 50],
                                          }: PaginationControlProps) {
    const {pagination, nextPage, prevPage, isLoading, setPerPage, setPage} = source;

    const onNavigate = (isNext: boolean) => {
        if (isNext) {
            nextPage();
        } else {
            prevPage();
        }
    }

    const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPerPage(Number(event.target.value));
    };

    const getPageNumbers = () => {
        const currentPage = pagination.currentPage || 1;
        const lastPage = pagination.lastPage || 1;
        const pages: (number | 'ellipsis')[] = [];

        pages.push(1);

        if (lastPage <= 7) {
            for (let i = 2; i <= lastPage; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage > 3) {
                pages.push('ellipsis');
            }

            const startPage = Math.max(2, currentPage - 1);
            const endPage = Math.min(lastPage - 1, currentPage + 1);

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (currentPage < lastPage - 2) {
                pages.push('ellipsis');
            }

            if (lastPage > 1) {
                pages.push(lastPage);
            }
        }

        return pages;
    };

    const currentPage = pagination.currentPage || 1;
    const lastPage = pagination.lastPage || 1;
    const showPageNumbers = lastPage > 1;
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === lastPage;

    return (
        <div className="grow flex items-center">
            <span className="shrink-0 text-xs text-gray-800 dark:text-gray-200">
                Page {currentPage} of {lastPage}
            </span>

            <div className="flex-grow flex justify-center">
                <div className="flex rounded-lg shadow-sm ring-1 transition duration-75 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 ring-gray-200 dark:ring-gray-700">
                    <div className="flex items-center gap-x-3 border-e border-gray-200 dark:border-gray-700 pe-1 ps-2 md:pe-3 md:ps-3">
                        <span className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            Per page
                        </span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <select
                            value={pagination.perPage || perPageOptions[0]}
                            onChange={handlePerPageChange}
                            className="block w-full border-none bg-transparent py-1.5 pe-8 text-xs md:text-sm text-gray-950 dark:text-gray-100 transition duration-75 focus:ring-0 disabled:text-gray-500 dark:disabled:text-gray-600 sm:text-sm sm:leading-6 md:ps-3"
                            disabled={isLoading}
                        >
                            {perPageOptions.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="shrink-0 flex justify-end">
                <button
                    onClick={() => onNavigate(false)}
                    disabled={isFirstPage || isLoading}
                    className={`flex md:hidden items-center justify-center w-8 h-7 md:h-8 text-sm font-medium border bg-white dark:bg-gray-800 transition-colors duration-200
                    ${isFirstPage || isLoading
                        ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                        : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
                    }
                    rounded-l-md`}
                    aria-label="Previous page"
                >
                    <svg
                        className="w-3.5 h-3.5 rtl:rotate-180"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>

                {!isFirstPage && (
                    <button
                        onClick={() => onNavigate(false)}
                        disabled={isLoading}
                        className={`hidden md:flex items-center justify-center w-8 h-8 text-sm font-medium border bg-white dark:bg-gray-800 transition-colors duration-200
                        ${isLoading
                            ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                            : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
                        }
                        rounded-l-lg`}
                        aria-label="Previous page"
                    >
                        <svg
                            className="w-3.5 h-3.5 rtl:rotate-180"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                )}

                {showPageNumbers && getPageNumbers().map((page, index, array) =>
                    page === 'ellipsis'
                        ? <Ellipsis key={`ellipsis-${index}`} />
                        : <PageNumber
                            key={page}
                            page={page}
                            isActive={page === currentPage}
                            isDisabled={isLoading}
                            isFirstPage={isFirstPage && index === 0}
                            isLastPage={isLastPage && index === array.length - 1}
                            onClick={() => {
                                if (page !== currentPage) {
                                    setPage(page, true);
                                }
                            }}
                        />
                )}

                {!isLastPage && (
                    <button
                        onClick={() => onNavigate(true)}
                        disabled={isLoading}
                        className={`hidden md:flex items-center justify-center w-8 h-8 text-sm font-medium border bg-white dark:bg-gray-800 transition-colors duration-200
                        ${isLoading
                            ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                            : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
                        }
                        rounded-r-lg border-l-0`}
                        aria-label="Next page"
                    >
                        <svg
                            className="w-3.5 h-3.5 rtl:rotate-180"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                )}

                <button
                    onClick={() => onNavigate(true)}
                    disabled={isLastPage || isLoading}
                    className={`flex md:hidden items-center justify-center w-8 h-7 md:h-8 text-sm font-medium border bg-white dark:bg-gray-800 transition-colors duration-200
                    ${isLastPage || isLoading
                        ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                        : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
                    }
                    rounded-r-md border-l-0`}
                    aria-label="Next page"
                >
                    <svg
                        className="w-3.5 h-3.5 rtl:rotate-180"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            </div>
        </div>
    )
}
